import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { User, AuthTokens } from '@vigilo/shared';
import { post, TOKEN_KEY } from '@vigilo/api-client';
import { useApp } from './AppContext';
import { useMockMode } from './MockModeContext';
import { appForUser, pathForUser, type VigiloAppId } from '@vigilo/security';

interface LoginResponse {
  data: { user: User; tokens: AuthTokens };
}

const DEMO_ACCOUNTS: Record<VigiloAppId, { label: string; email: string }[]> = {
  crowd: [
    { label: 'Analyst', email: 'analyst@vigilo.dev' },
    { label: 'Supervisor', email: 'supervisor@vigilo.dev' },
    { label: 'Admin', email: 'operator@vigilo.dev' },
  ],
  border: [
    { label: 'Ops Analyst', email: 'border.analyst@vigilo.dev' },
    { label: 'Intel', email: 'border.intel@vigilo.dev' },
    { label: 'Supervisor', email: 'border.supervisor@vigilo.dev' },
    { label: 'Admin', email: 'border.admin@vigilo.dev' },
  ],
  ci: [
    { label: 'Security Officer', email: 'ci.officer@vigilo.dev' },
    { label: 'Facility Manager', email: 'ci.manager@vigilo.dev' },
    { label: 'Supervisor', email: 'ci.supervisor@vigilo.dev' },
    { label: 'Admin', email: 'ci.admin@vigilo.dev' },
  ],
  forensics: [
    { label: 'Investigator', email: 'investigator@vigilo.dev' },
    { label: 'Senior Investigator', email: 'senior.investigator@vigilo.dev' },
    { label: 'Forensic Analyst', email: 'analyst@forensics.vigilo.dev' },
    { label: 'Supervisor', email: 'forensics.supervisor@vigilo.dev' },
    { label: 'Admin', email: 'forensics.admin@vigilo.dev' },
  ],
  intel: [
    { label: 'Intelligence Analyst', email: 'intel.analyst@vigilo.dev' },
    { label: 'Senior Intel Officer', email: 'intel.senior@vigilo.dev' },
    { label: 'Command Supervisor', email: 'intel.supervisor@vigilo.dev' },
    { label: 'Admin', email: 'intel.admin@vigilo.dev' },
  ],
};

const DEMO_PASSWORD = 'Vigilo2026!';
const APPLICATION_IDS: Record<VigiloAppId, string> = {
  crowd: 'crowd_monitoring',
  border: 'border_security',
  ci: 'critical_infrastructure',
  forensics: 'vigilo_forensics',
  intel: 'vigilo_intel',
};

export function LoginScreen() {
  const { setCurrentUser } = useApp();
  const { setMock } = useMockMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  const requestedApp = parseRequestedApp(searchParams.get('app'));
  const demoAccounts = DEMO_ACCOUNTS[requestedApp ?? 'crowd'];

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await post<LoginResponse>('/auth/login', {
        email,
        password,
        applicationId: APPLICATION_IDS[requestedApp ?? 'crowd'],
      });
      const { user, tokens } = res.data;
      localStorage.setItem(TOKEN_KEY(), tokens.accessToken);
      // A real credential login means the backend is reachable and the operator expects real data.
      // Otherwise a fresh dev-build browser would open on mock fixtures behind a live session.
      setMock(false);
      setCurrentUser(user);
      const app = appForUser(user, requestedApp);
      const requested = requestedApp;
      if (!app) {
        setError('This account is not assigned to a Vigilo application.');
        return;
      }
      if (requested && requested !== app) {
        setError(`This account is assigned to ${labelForApp(app)} and cannot access ${labelForApp(requested)}.`);
        return;
      }
      navigate(pathForUser(user, requestedApp), { replace: true });
    } catch (err) {
      setError(err instanceof Error && err.message !== 'Unauthorized'
        ? 'Unable to reach the authentication service. Please check that the API is running.'
        : 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function fillDemo(acc: { email: string }) {
    setEmail(acc.email);
    setPassword(DEMO_PASSWORD);
    setError(null);
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B1220',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Albert Sans', sans-serif",
    }}>
      <div style={{
        width: 360,
        background: '#111E33',
        border: '1px solid rgba(232,238,247,0.08)',
        borderRadius: 10,
        padding: '36px 32px',
      }}>
        <div style={{ marginBottom: 28, textAlign: 'center' }}>
          <div style={{
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: '0.06em',
            color: '#E8EEF7',
            textTransform: 'uppercase',
          }}>
            VIGILO
          </div>
          <div style={{ fontSize: 11, color: 'rgba(232,238,247,0.4)', marginTop: 2 }}>
            {labelForApp(requestedApp ?? 'crowd')}
          </div>
        </div>

        {searchParams.get('reason') === 'unauthorized' && (
          <div style={{
            fontSize: 12,
            color: '#D97706',
            marginBottom: 14,
            padding: '7px 10px',
            background: 'rgba(217,119,6,0.1)',
            border: '1px solid rgba(217,119,6,0.25)',
            borderRadius: 4,
          }}>
            Your current account is not authorised for this application. Sign in with the correct role.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'rgba(232,238,247,0.5)', marginBottom: 5, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Email
            </label>
            <input
              data-testid="login-email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: '#0B1220',
                border: '1px solid rgba(232,238,247,0.12)',
                borderRadius: 5,
                color: '#E8EEF7',
                fontSize: 13,
                padding: '9px 11px',
                outline: 'none',
                fontFamily: "'Albert Sans', sans-serif",
              }}
            />
          </div>

          <div style={{ marginBottom: 22 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'rgba(232,238,247,0.5)', marginBottom: 5, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Password
            </label>
            <input
              data-testid="login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: '#0B1220',
                border: '1px solid rgba(232,238,247,0.12)',
                borderRadius: 5,
                color: '#E8EEF7',
                fontSize: 13,
                padding: '9px 11px',
                outline: 'none',
                fontFamily: "'Albert Sans', sans-serif",
              }}
            />
          </div>

          {error && (
            <div
              data-testid="login-error"
              style={{
                fontSize: 12,
                color: '#C2410C',
                marginBottom: 14,
                padding: '7px 10px',
                background: 'rgba(194,65,12,0.1)',
                border: '1px solid rgba(194,65,12,0.25)',
                borderRadius: 4,
              }}
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: loading ? 'rgba(217,119,6,0.5)' : '#D97706',
              color: loading ? 'rgba(232,238,247,0.5)' : '#0B1220',
              border: 'none',
              borderRadius: 5,
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "'Albert Sans', sans-serif",
              padding: '10px 0',
              cursor: loading ? 'not-allowed' : 'pointer',
              letterSpacing: '0.04em',
            }}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <div style={{ marginTop: 20, borderTop: '1px solid rgba(232,238,247,0.07)', paddingTop: 14 }}>
          <button
            type="button"
            onClick={() => setShowDemo(v => !v)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'rgba(232,238,247,0.35)',
              fontSize: 10.5,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: 0,
              fontFamily: "'Albert Sans', sans-serif",
            }}
          >
            <span style={{ display: 'inline-block', transform: showDemo ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>{'>'}</span>
            Demo accounts
          </button>
          {showDemo && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ fontSize: 9.5, color: 'rgba(232,238,247,0.25)', marginBottom: 3 }}>
                Password: {DEMO_PASSWORD}
              </div>
              {demoAccounts.map(acc => (
                <button
                  key={acc.email}
                  type="button"
                  onClick={() => fillDemo(acc)}
                  style={{
                    background: '#0B1220',
                    border: '1px solid rgba(232,238,247,0.1)',
                    borderRadius: 4,
                    padding: '6px 10px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontFamily: "'Albert Sans', sans-serif",
                  }}
                >
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(232,238,247,0.7)' }}>{acc.label}</span>
                  <span style={{ fontSize: 9.5, color: 'rgba(232,238,247,0.35)', fontFamily: 'monospace' }}>{acc.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function parseRequestedApp(value: string | null): VigiloAppId | null {
  return value === 'ci' || value === 'border' || value === 'crowd' || value === 'forensics' || value === 'intel' ? value : null;
}

function labelForApp(app: VigiloAppId): string {
  if (app === 'ci') return 'Critical Infrastructure';
  if (app === 'border') return 'Border Security';
  if (app === 'forensics') return 'Vigilo Forensics';
  if (app === 'intel') return 'Vigilo Intel';
  return 'Crowd Monitoring';
}
